import { State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { StudioDeviceControlPreset } from '@tryiris-ai/common.protojs/studio/v1/studio';
import { normalizeId } from '@studio/models';
import { AgentConfigGet } from '@tryiris-ai/mailroom-helper/agentConfig';
import { locateProxy } from '#~/agent/index.js';
import { toIdFromHex, wrapApiCall } from '#~/api/index.js';
import { ElectronAPI } from '#~/electron/api.js';
import { nats } from '#~/nats/index.js';
import { comparatorFn } from '#~/models/utils.js';
import { addGroup, removeGroup } from '#~/control/preset/commands/index.js';
import type { Device } from '@studio/models/device';
import type { Group } from '@studio/models/control/preset';
import type { ReadonlyDeep } from 'type-fest';
import type { Agent } from '@studio/models/agent';

type Snapshot = {
  agentId: string;
  embedded: boolean;
  groups: Array<{ id: string; name: string; rank: number }>;
  presets: Array<{ id: string; name: string; group: string; rank: number }>;
};

/** Complete preset comparisons use hashes so native targets and thumbnail URLs stay inside the renderer. */
type Contents = Snapshot & { signatures: Array<{ id: string; signature: string }> };
type OwnedGroup = { id: string; names: string[] };

/** One uncached reader serves both reorder and rename checks, never optimistic state. */
async function storedState(deviceId: Device['id']): Promise<State> {
  const proxy = owner(deviceId);
  const agentId = toIdFromHex(proxy.id);
  if (agentId === undefined) throw new Error('Invalid preset-group owner');
  const token = await ElectronAPI.getAccessToken();
  const response = await wrapApiCall(
    () =>
      AgentConfigGet.request(
        nats.cloudConnection(),
        { agentId, token, nativePresetsVersion: 1 },
        { force: true, staleOnError: false }
      ),
    'Unable to verify saved preset groups'
  );
  return State.decode(response.config);
}

/** Avoid hand-selecting preset fields: any stored shot change must fail a name-only operation. */
async function contents(
  state: ReadonlyDeep<State>,
  deviceId: Device['id'],
  agentId: Agent['id'],
  embedded: boolean
): Promise<Contents> {
  if (embedded !== true) throw new Error('Preset-group contents require an embedded agent');
  const snapshot = project(state, deviceId, agentId, embedded);
  const items = state.control?.studio?.device?.[deviceId]?.control?.presets?.items;
  if (items === undefined) throw new Error('Missing preset collection');
  const signatures = await Promise.all(
    Object.entries(items).map(async ([id, item]) => {
      const bytes = StudioDeviceControlPreset.encode(
        StudioDeviceControlPreset.fromPartial(item)
      ).finish();
      const digest = await crypto.subtle.digest('SHA-256', Uint8Array.from(bytes));
      return {
        id,
        signature: Array.from(new Uint8Array(digest), (byte) =>
          byte.toString(16).padStart(2, '0')
        ).join('')
      };
    })
  );
  return { ...snapshot, signatures: signatures.sort((a, b) => a.id.localeCompare(b.id)) };
}

/** Per secret-bearing-document-output (ai.knowledge/global): never serialize raw error messages. */
async function withFailureDetails<T>(operation: string, action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch (cause) {
    // Playwright drops Error.cause; retain only classifications and built-asset coordinates in its message.
    const causes: unknown[] = [];
    const seen = new Set<unknown>();
    const names = new Set([
      'Error',
      'StudioError',
      'TypeError',
      'RangeError',
      'SyntaxError',
      'TimeoutError'
    ]);
    const kinds = new Map([
      ['Unable to add preset group.', 'add-group'],
      ['Unable to remove preset group.', 'remove-group'],
      ['Unable to save the agent state.', 'save-agent'],
      ['Cannot convert undefined or null to object', 'missing-container'],
      ['CG-213', 'missing-owner'],
      ['DG-244', 'missing-owner']
    ]);
    let current: unknown = cause;
    while (current && typeof current === 'object' && !seen.has(current) && causes.length < 8) {
      seen.add(current);
      const error = current as {
        name?: unknown;
        message?: unknown;
        code?: unknown;
        stack?: unknown;
        cause?: unknown;
      };
      const message = typeof error.message === 'string' ? error.message : '';
      const kind =
        kinds.get(message) ??
        (message.startsWith('Cannot read properties of undefined') ||
        message.startsWith('Cannot read properties of null')
          ? 'missing-property'
          : message.endsWith(' is not a function')
            ? 'not-callable'
            : message.endsWith(' is not iterable')
              ? 'not-iterable'
              : message.startsWith('Unable to set stored data:')
                ? 'storage-write'
                : message.startsWith('Unable to get stored data:')
                  ? 'storage-read'
                  : 'unknown');
      causes.push({
        name: typeof error.name === 'string' && names.has(error.name) ? error.name : 'unknown',
        kind,
        code:
          typeof error.code === 'number' &&
          Number.isInteger(error.code) &&
          error.code >= 100 &&
          error.code <= 599
            ? error.code
            : undefined,
        frames:
          typeof error.stack === 'string'
            ? Array.from(
                // Entry plus every manual chunk in vite.config.js; protobuf frames sit in eta/zeta.
                error.stack.matchAll(
                  /\b(?:index|alpha|beta|delta|epsilon|eta|zeta)(?:-[A-Za-z0-9_]+)?\.js:\d+:\d+/g
                ),
                (match) => match[0]
              ).slice(0, 6)
            : []
      });
      current = error.cause;
    }
    throw new Error(`Preset-group ${operation} failed: ${JSON.stringify(causes)}`, { cause });
  }
}

function owner(deviceId: Device['id']) {
  const proxy = locateProxy(deviceId);
  if (!proxy) throw new Error(`No agent owns preset-group camera ${deviceId}`);
  return proxy;
}

/** Positive allowlist: never expose camera credentials or the full saved configuration. */
function project(
  state: ReadonlyDeep<State>,
  deviceId: Device['id'],
  agentId: Agent['id'],
  embedded: boolean
): Snapshot {
  const presets = state.control?.studio?.device?.[deviceId]?.control?.presets;
  if (!presets || Object.keys(presets.groups).length === 0)
    throw new Error(`No saved preset groups for camera ${deviceId}`);
  return {
    agentId,
    embedded,
    groups: Object.entries(presets.groups)
      .map(([id, group]) => ({ id, name: group.name ?? '', rank: group.rank ?? 0 }))
      .sort(comparatorFn),
    presets: Object.entries(presets.items)
      .map(([id, preset]) => ({
        id,
        name: preset.name ?? '',
        group: preset.group ?? '',
        rank: preset.rank ?? 0
      }))
      .sort((a, b) => a.id.localeCompare(b.id))
  };
}

// Setup and cleanup use real commands; the tested actions always come from user input.
export const presetGroupProbe = {
  documentId: performance.timeOrigin,
  current(deviceId: Device['id']): Snapshot {
    const proxy = owner(deviceId);
    return project(proxy.state, deviceId, proxy.id, proxy.embedded);
  },
  async persisted(deviceId: Device['id']): Promise<Snapshot> {
    const proxy = owner(deviceId);
    // See state-sync-optimistic-mutations (ai.knowledge/platform/studio.client): local state is not a save receipt.
    return project(await storedState(deviceId), deviceId, proxy.id, proxy.embedded);
  },
  async contents(deviceId: Device['id'], stored = false): Promise<Contents> {
    return withFailureDetails('contents', async () => {
      const proxy = owner(deviceId);
      return contents(
        stored ? await storedState(deviceId) : proxy.state,
        deviceId,
        proxy.id,
        proxy.embedded
      );
    });
  },
  async cleanupOwned(
    deviceId: Device['id'],
    original: Contents,
    owned: OwnedGroup[]
  ): Promise<void> {
    return withFailureDetails('cleanup-owned', async () => {
      const proxy = owner(deviceId);
      if (proxy.id !== original.agentId || proxy.embedded !== true)
        throw new Error('Preset-group owner changed');
      const collection = proxy.state.control?.studio?.device?.[deviceId]?.control?.presets;
      if (collection === undefined) throw new Error('Missing preset collection');
      // Validate every owned identity before the first write. Never overwrite baseline names or ranks.
      const groupIds = owned.map((group) => {
        const id = normalizeId<Group['id']>(group.id);
        if (id === undefined) throw new Error('Invalid owned group');
        return id;
      });
      for (const group of owned) {
        const current = collection.groups[group.id];
        if (
          original.groups.some((g) => g.id === group.id) ||
          (current !== undefined && !group.names.includes(current.name ?? ''))
        )
          throw new Error('Preset-group cleanup ownership changed');
      }
      // Owned groups are created empty. A shot inside one was placed there by something other
      // than this automation, so removing the group would delete data the test does not own.
      if (Object.values(collection.items).some((shot) => owned.some((g) => g.id === shot.group)))
        throw new Error('Preset found inside an owned group');
      const token = await ElectronAPI.getAccessToken();
      for (const groupId of groupIds) {
        // A group the test itself deleted is already gone; another save would only add a write.
        if (collection.groups[groupId] === undefined) continue;
        await removeGroup(deviceId, groupId, token);
      }
    });
  },
  async create(deviceId: Device['id'], name: string, rank: number): Promise<string> {
    return withFailureDetails('create', async () =>
      addGroup(deviceId, { name, rank }, await ElectronAPI.getAccessToken())
    );
  },
  async cleanup(deviceId: Device['id'], original: Snapshot, owned: string[]): Promise<void> {
    const proxy = owner(deviceId);
    if (proxy.id !== original.agentId) throw new Error('Preset-group owner changed during cleanup');
    await withFailureDetails('cleanup', async () => {
      const token = await ElectronAPI.getAccessToken();
      for (const id of owned) await removeGroup(deviceId, id as Group['id'], token);
      // Restore exact original ranks, which need not have been contiguous.
      await proxy.mutate(
        {
          control: {
            studio: {
              device: {
                [deviceId]: {
                  control: {
                    presets: {
                      groups: Object.fromEntries(
                        original.groups.map((group) => [group.id, { rank: group.rank }])
                      )
                    }
                  }
                }
              }
            }
          }
        },
        {}
      );
      await proxy.save(token);
    });
  }
};

// Per writing-hil-e2e-tests: the preload runtime flag keeps these methods out of normal UI use.
if (
  (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } }).window
    ?.electron?.process?.env?.IRIS_E2E_TEST === '1'
) {
  Object.assign(globalThis, { __irisPresetGroupProbe: presetGroupProbe });
}
